import React, { useEffect, useState } from 'react';
import { db, functions } from '../firebase';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

export const ApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    const fetchApps = async () => {
      try {
        const q = query(collection(db, 'driver_applications'), where('status', '==', 'pending'));
        const snap = await getDocs(q);
        const apps = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setApplications(apps);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load applications');
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, []);

  const approveApplication = async (app) => {
    if (!window.confirm(`Approve ${app.fullName} as a driver?`)) return;
    try {
      // Create driver document
      await db.collection('drivers').doc(app.uid).set({
        uid: app.uid,
        email: app.email,
        fullName: app.fullName,
        phone: app.phone,
        idNumber: app.idNumber,
        kycStatus: 'approved',
        documents: app.documents,
        createdAt: new Date(),
        updatedAt: new Date()
      }, { merge: true });
      
      // Update application status
      await updateDoc(doc(db, 'driver_applications', app.id), { status: 'approved', reviewedAt: new Date() });
      
      // Set custom claim for driver role (optional)
      const setDriverClaim = httpsCallable(functions, 'setDriverClaim');
      await setDriverClaim({ uid: app.uid });
      
      toast.success(`${app.fullName} approved as driver`);
      setApplications(prev => prev.filter(a => a.id !== app.id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const rejectApplication = async (app) => {
    if (!window.confirm(`Reject ${app.fullName}?`)) return;
    try {
      await updateDoc(doc(db, 'driver_applications', app.id), { status: 'rejected', reviewedAt: new Date() });
      toast.success(`Application rejected`);
      setApplications(prev => prev.filter(a => a.id !== app.id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <div className="text-gray-400 p-4">Loading applications...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Driver Applications</h2>
      {applications.length === 0 ? <p className="text-gray-400">No pending applications.</p> : (
        <div className="grid gap-4">
          {applications.map(app => (
            <div key={app.id} className="bg-gray-800 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-lg">{app.fullName}</p>
                  <p className="text-gray-400 text-sm">{app.email} | {app.phone}</p>
                  <p className="text-gray-400 text-sm">ID: {app.idNumber}</p>
                  <button onClick={() => setSelectedApp(app)} className="text-emerald-400 text-sm mt-2">View Documents</button>
                </div>
                <div className="space-x-2">
                  <button onClick={() => approveApplication(app)} className="bg-emerald-600 px-3 py-1 rounded">Approve</button>
                  <button onClick={() => rejectApplication(app)} className="bg-red-600 px-3 py-1 rounded">Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedApp && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setSelectedApp(null)}>
          <div className="bg-gray-900 p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-4">Documents for {selectedApp.fullName}</h3>
            {Object.entries(selectedApp.documents).map(([key, url]) => (
              <div key={key} className="mb-3">
                <p className="text-sm text-gray-400 capitalize">{key}</p>
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 underline break-all">View Document</a>
              </div>
            ))}
            <button onClick={() => setSelectedApp(null)} className="mt-4 bg-gray-600 px-4 py-2 rounded">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};
